// src/components/GuideDownload.jsx
import React, { useState } from "react";
import { apiPOST, normalizeApiUrl } from "../lib/api";
import { textOnlyOptions, formatIntl } from "../lib/countries";
import Portal from "./Portal";
import DownloadToast from "./DownloadToast";

// --- helpers -------------------------------------------------
function pdfNameFrom(title = "download") {
  const base = String(title)
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .toLowerCase();
  return `${base}.pdf`;
}



/** Strip +CC/00CC/leading 0 from the local field (handles autofill). */
function scrubLocal(raw = "", cc = "+44") {
  let v = String(raw).replace(/[\s-]+/g, "");
  const ccNum = String(cc || "+").replace("+", "");
  if (ccNum) {
    const re = new RegExp(`^(?:\\+|00)?${ccNum}\\s*0?`);
    v = v.replace(re, "");
  }
  v = v.replace(/^\+/, "");
  return v;
}
// -------------------------------------------------------------

function SuccessModal({ open, onClose, url, fileName }) {
  if (!open) return null;
  return (
    <Portal>
      <div className="fixed inset-0 z-[2050] bg-black/40" role="dialog" aria-modal="true">
        <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
          <div className="relative w-full max-w-md rounded-2xl bg-white shadow-xl ring-1 ring-black/5">
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 rounded-full p-2 text-[#003366] hover:bg-slate-100"
              aria-label="Close"
            >
              ✕
            </button>

            <div className="px-6 pt-6 pb-4">
              <h3 className="text-lg sm:text-xl font-semibold text-[#003366]">You’re all set ✅</h3>
              <p className="mt-2 text-sm text-[#003366]">
                Thanks — your details were submitted successfully. Your guide is downloading now.
              </p>
<p className="mt-2 text-xs text-slate-700">
  One of the team may reach out to understand your goals and share tailored insights. If you’d like to speak sooner, you can{" "}
  <a
    href="https://wa.me/442038852754?text=Hi%2C%20I%E2%80%99d%20like%20to%20discuss%20my%20investment%20plans."
    target="_blank" rel="noreferrer"
    className="font-medium underline underline-offset-2 text-[#003366]"
  >
    chat with us on WhatsApp
  </a>.
</p>
              <div className="mt-4 flex gap-2">
                <a href={url} download={fileName} className="rounded-lg bg-[#003366] px-3 py-2 text-sm font-medium text-white hover:bg-slate-800">
                  Download again
                </a>
                <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-[#003366] hover:bg-slate-50">
                  Close
                </button>
              </div>

              <p className="mt-3 text-xs text-slate-600">
                If nothing happens, use the button above to download manually.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}

export default function GuideDownload({
  guideId,
  guideTitle,
  slug,
  className = "",
  variant = "default",
  label = "Download PDF",
  buttonClassName,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // success modal + toast state (toast gated by success modal)
  const [successOpen, setSuccessOpen] = useState(false);
  const [downloadUrlState, setDownloadUrlState] = useState("");

  const solid =
    "rounded-xl bg-slate-900 text-white px-4 py-2 hover:bg-slate-800 disabled:opacity-50";
  const compact =
    "inline-flex items-center rounded-full bg-slate-900 text-white px-3 py-1 text-sm font-medium hover:bg-slate-800 disabled:opacity-50";
  const btnCls = buttonClassName || (variant === "compact" ? compact : solid);

  function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email).trim());
}

function isValidPhone(phone) {
  // accept 7–15 digits, optional + prefix
  return /^\+?\d{7,15}$/.test(String(phone).replace(/\s+/g, ""));
}

async function onSubmit(e) {
  e.preventDefault();
  setErr(null);

  // --- read form first ---
  const form = new FormData(e.currentTarget);
  if ((form.get("website") || "").trim()) return; // honeypot

  const fullName    = String(form.get("fullName") || "").trim();
  const email       = String(form.get("email") || "").trim();
  const countryCode = String(form.get("countryCode") || "");
  const rawLocal    = String(form.get("phoneLocal") || "").trim();
  const phoneLocal  = scrubLocal(rawLocal, countryCode); // sanitize
  const message     = String(form.get("message") || "").trim();
  const consent     = form.get("consent") === "on";
  const readyToInvest  = String(form.get("readyToInvest") || "");
  const amountToInvest = String(form.get("amountToInvest") || "");
  const phone = formatIntl(countryCode, phoneLocal);

  // --- now validate using the values you just read ---
  if (!isValidEmail(email)) {
    setErr("Please enter a valid email address (e.g. name@example.com).");
    return;
  }
  if (!isValidPhone(phoneLocal)) {
    setErr("Please enter a valid phone number (digits only).");
    return;
  }
  if (!slug && !guideId) {
    setErr("Internal configuration error: missing guide id/slug.");
    return;
  }
  if (!fullName || !email || !phoneLocal || !consent) {
    setErr("Please complete required fields (Name, Email, Phone) and consent.");
    return;
  }

  setLoading(true);
  try {
    const payload = {
      source: "download",
      guide: guideId || slug,
      id: guideId || slug,
      name: fullName,
      email,
      phone,
      message,
      readyToInvest,
      amountToInvest,
      consent,
      slug,
      guideId,
      guideTitle,
    };

    // ***** SHEET WRITE — EXACTLY LIKE pages/Enquire.jsx *****
    const sheetData = {
      name: fullName,
      email,
      countryCode,
      phone, // formatted intl
      message,
      readyToInvest,
      amountToInvest,
      consent: consent ? "yes" : "no",
      timestamp: new Date().toISOString(),
      source: "download-guide",
      guide: guideTitle || slug,
      id: guideTitle || slug,
    };

    {
      const res = await fetch("/enquire", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body: new URLSearchParams(sheetData),
      });
      const text = await res.text();
      let ok = res.ok;
      let serverErr = "";
      try {
        const j = JSON.parse(text);
        if (typeof j?.ok === "boolean") ok = j.ok;
        if (j?.error) serverErr = j.error;
      } catch {}
      if (!ok) throw new Error(serverErr || "Submit failed");
    }
    // ***** END SHEET WRITE *****

    const finalLink = `/api/guides/download?guide=${encodeURIComponent(guideId || slug)}`;
    const url = await normalizeApiUrl(finalLink);

    setOpen(false);
    setDownloadUrlState(url);
    setSuccessOpen(true);

    // Try programmatic download; fall back to opening the URL
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = pdfNameFrom(guideTitle || slug || "guide");
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      window.open(url, "_blank", "noopener");
    }

    window.gtag?.("event", "file_download", {
      file_name: (guideTitle || slug || "guide") + ".pdf",
      file_type: "pdf",
      link_domain: window.location.hostname,
      guide_id: guideId || slug,
    });
    window.fbq?.("trackCustom", "GuideDownload", {
      guide: guideId || slug,
      title: guideTitle || slug,
    });
  } catch (e2) {
    setErr(e2.message || "Something went wrong while processing your request.");
  } finally {
    setLoading(false);
  }
}


  const fileName = pdfNameFrom(guideTitle || slug || "guide");

  return (
    <div className={className}>
      <button onClick={() => setOpen(true)} className={btnCls}>
        {label}
      </button>

      {open && (
        <Portal>
          <div className="fixed inset-0 z-[2000] bg-black/40" role="dialog" aria-modal="true">
            <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
              <div className="relative w-full max-w-lg sm:max-w-xl rounded-2xl bg-white shadow-xl ring-1 ring-black/5">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="absolute right-3 top-3 rounded-full p-2 text-[#003366] hover:bg-slate-100"
                  aria-label="Close"
                >
                  ✕
                </button>

                <div className="px-5 sm:px-6 pt-5 pb-2">
                  <h3 className="text-lg sm:text-xl font-semibold text-[#003366]">A few details to access the guide</h3>
                  <p className="mt-1 text-sm text-[#003366]">Fill in your details to get the e-guide.</p>
                </div>

                <div className="px-5 sm:px-6 pb-5 max-h-[75vh] overflow-y-auto">
                  <form onSubmit={onSubmit} className="space-y-4">
                    <input name="website" className="hidden" tabIndex={-1} autoComplete="off" />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#003366]">
                          Full name<span className="text-red-500">*</span>
                        </label>
                        <input
                          name="fullName"
                          required
                          autoComplete="name"
                          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-base"
                          disabled={loading}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#003366]">
                          Email<span className="text-red-500">*</span>
                        </label>
                        <input
                          name="email"
                          type="email"
                          required
                          autoComplete="email"
                          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-base"
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium">Phone</label>
                      <div className="mt-1 flex gap-2">
                        <select
                          name="countryCode"
                          required
                          defaultValue="+44"
                          className="rounded border px-3 py-2"
                          disabled={loading}
                          autoComplete="tel-country-code"
                        >
                          {textOnlyOptions().map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                        <input
                          name="phoneLocal"
                          required
                          inputMode="tel"
                          autoComplete="tel-national"
                          className="flex-1 rounded border px-3 py-2"
                          disabled={loading}
                          onInput={(e) => {
                            const sel = e.currentTarget.form?.countryCode?.value || "+44";
                            e.currentTarget.value = scrubLocal(e.currentTarget.value, sel);
                          }}
                          onPaste={(e) => {
                            const sel = e.currentTarget.form?.countryCode?.value || "+44";
                            const t = (e.clipboardData || window.clipboardData).getData("text");
                            e.currentTarget.value = scrubLocal(t, sel);
                            e.preventDefault();
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm">When are you planning to make your next investment?</label>
                      <select name="readyToInvest" required className="w-full rounded border px-3 py-2" defaultValue="" disabled={loading}>
                        <option value="" disabled>Select an option…</option>
                        <option>Ready to invest now.</option>
                        <option>Within 3 months.</option>
                        <option>3-6 months.</option>
                        <option>Just exploring for now.</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm">What level of funds are you planning to allocate?</label>
                      <select name="amountToInvest" required className="w-full rounded border px-3 py-2" defaultValue="" disabled={loading}>
                        <option value="" disabled>Select an option…</option>
                        <option>Under £100,000</option>
                        <option>£100,000 – £250,000</option>
                        <option>£250,000 - £500,000</option>
                        <option>£500,000 plus</option>
                        <option>Unknown</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#003366]">Message (optional)</label>
                      <input
                        name="message"
                        placeholder="Anything you'd like us to know?"
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-base"
                        disabled={loading}
                      />
                    </div>

                    <label className="flex items-start gap-2 text-sm">
                      <input name="consent" type="checkbox" className="mt-1" required disabled={loading} />
                      <span>I agree to be contacted about related services and have read the privacy notice.</span>
                    </label>

                    {err && <p className="text-sm text-red-600">{err}</p>}

                    <div className="pt-2 grid grid-cols-1 sm:flex sm:items-center sm:justify-end gap-3">
                      <button disabled={loading} className="w-full sm:w-auto rounded-xl bg-[#003366] text-white px-4 py-2 text-base hover:bg-slate-800 disabled:opacity-60">
                        {loading ? "Processing…" : "Unlock & download"}
                      </button>
                      <button type="button" onClick={() => setOpen(false)} className="w-full sm:w-auto rounded-xl border border-slate-300 px-4 py-2 text-base text-[#003366] hover:bg-slate-50">
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Toast only shows when the modal is closed */}
            {!successOpen && downloadUrlState && (
              <DownloadToast url={downloadUrlState} onClose={() => setDownloadUrlState("")} />
            )}
          </div>
        </Portal>
      )}

      {/* Success confirmation modal shown after submit */}
      <SuccessModal
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        url={downloadUrlState}
        fileName={fileName}
      />
    </div>
  );
}
